/**
 * Motion hooks for the tutorial: the viewer's reduced-motion preference and a
 * pause flag while the document is hidden (eight infinite loops must not spin
 * behind another tab).
 * For Media Devoted | Created By Ciro Autiero
 */
import { CSSProperties, useEffect, useState } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

/** The media query list, or null where matchMedia is absent or stubbed. */
function reducedMotionQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(QUERY) ?? null;
}

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => reducedMotionQuery()?.matches ?? false);
  useEffect(() => {
    const mql = reducedMotionQuery();
    if (!mql) return;
    const onChange = () => setReduced(mql.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

export function useVisibilityPause(): boolean {
  const [hidden, setHidden] = useState(
    () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
  );
  useEffect(() => {
    const onChange = () => setHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return hidden;
}

/** Stagger helper: the delay of the i-th tick of a cycle, as inline style. */
export const tick = (i: number, seconds = 0.9): CSSProperties => ({
  animationDelay: `${(i * seconds).toFixed(2)}s`,
});
